/**
 * The one canonical form of an offline recovery code (AC-38, Immutable Rule 17).
 *
 * Shape: 32 CSPRNG bytes -> RFC 4648 base32 with NO padding -> 52 characters ->
 * displayed once as 13 hyphen-separated groups of 4. Grouping is pinned here so
 * the save state, the entry field, and the server all agree on one rendering.
 *
 * Canonical form for storage and comparison is the code with every separator and
 * whitespace removed and the alphabet lowercased. Only that string is HMAC'd, so
 * a person may re-type the code with any grouping, any case, and stray spaces.
 * The client canonicalises for the user's convenience; the server canonicalises
 * again and never trusts the client's version.
 */

/** Immutable Rule 17 — single-use tokens carry 32 CSPRNG bytes. */
export const RECOVERY_CODE_BYTES = 32;

/** 32 bytes = 256 bits = 52 base32 characters once the padding is dropped. */
export const RECOVERY_CODE_CHARS = 52;

/** Pinned display grouping: 52 characters divide evenly into 13 groups of 4. */
export const RECOVERY_CODE_GROUP_SIZE = 4;

/** RFC 4648 base32, lowercased — the case a user is asked to read back. */
const ALPHABET = "abcdefghijklmnopqrstuvwxyz234567";
/** Derived from RECOVERY_CODE_CHARS so the length cannot drift from the constant. */
const CANONICAL_PATTERN = new RegExp(`^[a-z2-7]{${RECOVERY_CODE_CHARS}}$`);
/** Hoisted: rebuilding this per render is pure waste on a hot display path. */
const GROUP_PATTERN = new RegExp(`.{1,${RECOVERY_CODE_GROUP_SIZE}}`, "g");

/** Encodes exactly `RECOVERY_CODE_BYTES` into the canonical unpadded base32 form. */
export function encodeRecoveryCodeBase32(bytes: Uint8Array): string {
  if (bytes.length !== RECOVERY_CODE_BYTES) {
    throw new Error("Recovery code material must be 32 bytes");
  }
  let bits = 0;
  let accumulator = 0;
  let encoded = "";
  for (const byte of bytes) {
    accumulator = (accumulator << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      encoded += ALPHABET[(accumulator >>> bits) & 0b11111];
    }
  }
  // 256 bits leave 1 bit over; the final character pads it with zeroes.
  if (bits > 0) encoded += ALPHABET[(accumulator << (5 - bits)) & 0b11111];
  return encoded;
}

/**
 * Decodes a canonical 52-character code, rejecting any encoding whose trailing
 * pad bits are non-zero — two spellings must never decode to the same secret.
 */
export function decodeRecoveryCodeBase32(canonical: string): Uint8Array | null {
  if (!CANONICAL_PATTERN.test(canonical)) return null;
  const bytes = new Uint8Array(RECOVERY_CODE_BYTES);
  let bits = 0;
  let accumulator = 0;
  let written = 0;
  for (const character of canonical) {
    accumulator = (accumulator << 5) | ALPHABET.indexOf(character);
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      bytes[written] = (accumulator >>> bits) & 0xff;
      written += 1;
    }
  }
  // 52 characters always yield exactly 32 bytes, so this is an invariant check
  // rather than a reachable branch — it fires only if the length guard above and
  // RECOVERY_CODE_BYTES ever disagree.
  if (written !== RECOVERY_CODE_BYTES) {
    throw new Error("Recovery code length constants disagree");
  }
  const padBits = accumulator & ((1 << bits) - 1);
  return padBits === 0 ? bytes : null;
}

/**
 * Strips separators and whitespace, lowercases, then validates charset, length,
 * and canonical encoding. Returns null for anything that is not a real code —
 * validation always precedes any HMAC of the value.
 */
export function canonicaliseRecoveryCode(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const stripped = value.replace(/[-\s]/g, "").toLowerCase();
  if (!CANONICAL_PATTERN.test(stripped)) return null;
  return decodeRecoveryCodeBase32(stripped) ? stripped : null;
}

/**
 * Renders the pinned display grouping.
 *
 * Throws on anything that is not a recovery code rather than echoing it back.
 * Returning the input untouched was worse: the one-time save state is the ONLY
 * time a user ever sees this value, so silently rendering a malformed string as
 * if it were their recovery code would send them away having written down
 * something that can never be redeemed. Failing loudly at the seam is the only
 * honest option on a display path with no second chance.
 */
export function formatRecoveryCodeForDisplay(value: string): string {
  const canonical = canonicaliseRecoveryCode(value);
  if (!canonical) throw new Error("Not a canonical recovery code");
  return (canonical.match(GROUP_PATTERN) ?? [canonical]).join("-");
}
